// Read-only rule helpers shared by validation, legalActions, bots, and the UI.

import { DECK, isPlayable, type Card, type Suit } from './cards';
import type { GameConfig, GameState, HandState, RulesConfig } from './types';

export const MIN_PLAYERS = 2;
export const MAX_PLAYERS = 8;
/** Points in every hand. Bidding 13 is an ordinary (if bold) bid worth 13. */
export const HAND_POINTS = 13;
/** Shooting the moon outbids 13: take all 13 points for 26, or lose 26. */
export const MOON_BID = 14;
export const MOON_SCORE = 26;

/** Points the bidder's side must take to make `bid`. */
export function pointsNeeded(bid: number): number {
  return Math.min(bid, HAND_POINTS);
}

export const DEFAULT_RULES: RulesConfig = {
  targetScore: 100,
  minBid: 4,
  kittySize: 'auto',
  partnerWithThree: true,
  handLimit: 6,
};

/**
 * Kitty size for a table. A configured size is a preference: the kitty is the
 * size from 4–6 closest to it that deals the rest evenly, so it never balloons
 * past 6 (for 2–8 players such a size always exists).
 */
export function kittySizeFor(playerCount: number, rules: RulesConfig): number {
  const preferred = rules.kittySize === 'auto' ? 4 : rules.kittySize;
  const even = [4, 5, 6].filter((k) => (DECK.length - k) % playerCount === 0);
  if (even.length === 0) return preferred + ((DECK.length - preferred) % playerCount);
  return even.reduce((best, k) => (Math.abs(k - preferred) < Math.abs(best - preferred) ? k : best));
}

export function partnersEnabled(config: GameConfig): boolean {
  if (config.playerCount <= 2) return false;
  if (config.playerCount === 3) return config.rules.partnerWithThree;
  return true;
}

export function nextSeat(config: GameConfig, seat: number): number {
  return (seat + 1) % config.playerCount;
}

/** Highest bid so far (0 if none). */
export function highBid(hand: HandState): number {
  return Math.max(0, ...hand.bids.map((b) => b ?? 0));
}

/** Seats still in the auction (haven't passed). */
export function biddersLeft(hand: HandState): number[] {
  return hand.bids.flatMap((b, i) => (b === 0 ? [] : [i]));
}

/** The bid values `player` may make right now (0 = pass), assuming it's their turn. */
export function bidOptions(state: GameState, player: number): number[] {
  const hand = state.hand;
  const high = highBid(hand);
  const options: number[] = [];
  // Nobody has bid and everyone else passed (that's the dealer): stuck with a bid.
  const stuck = high === 0 && biddersLeft(hand).every((i) => i === player);
  if (!stuck) options.push(0);
  for (let b = Math.max(state.config.rules.minBid, high + 1); b <= MOON_BID; b++) options.push(b);
  return options;
}

export function playableIn(cards: readonly Card[], trump: Suit): Card[] {
  return cards.filter((c) => isPlayable(c, trump));
}

/** What `player` must keep in the trim step: how many, which are forced, and what they choose from. */
export function keepRule(state: GameState, player: number): { count: number; required: Card[]; options: Card[] } {
  const hand = state.hand;
  const options = playableIn(hand.hands[player] ?? [], hand.trump!);
  const required = hand.called && options.includes(hand.called) ? [hand.called] : [];
  return { count: Math.min(state.config.rules.handLimit, options.length), required, options };
}

/** Kitty cards the bidder may pass to the partner. */
export function givableCards(state: GameState): Card[] {
  const hand = state.hand;
  if (hand.bidder === null || hand.trump === null) return [];
  return hand.kittyCards.filter(
    (c) => isPlayable(c, hand.trump!) && c !== hand.called && hand.hands[hand.bidder!]!.includes(c),
  );
}

/** Who holds the called card right now (the partner, or the bidder when alone). */
export function calledHolder(hand: HandState): number | null {
  if (!hand.called) return null;
  const i = hand.hands.findIndex((h) => h.includes(hand.called!));
  return i < 0 ? null : i;
}

/** Seats that may act right now (several during the trim, where everyone chooses at once). */
export function waitingOn(state: GameState): number[] {
  const p = state.phase;
  switch (p.kind) {
    case 'bid':
    case 'play':
      return [p.turn];
    case 'call':
    case 'pass':
      return [state.hand.bidder!];
    case 'trim':
      return p.pending.flatMap((x, i) => (x ? [i] : []));
    case 'gameOver':
      return [];
  }
}
